import { attr, html, MapSignal, Value } from '@tempots/dom'
import { Query } from '@tempots/ui'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export interface MarkdownSectionOptions {
  url: Value<string>
  background?: Value<'white' | 'light' | 'dark'>
}

export function MarkdownSection({
  url,
  background = 'white',
}: MarkdownSectionOptions) {
  return html.section(
    attr.class(
      'py-12 px-4 sm:px-6 md:px-8 lg:px-12 flex items-center justify-center'
    ),
    attr.class(
      Value.map(background, (bg): string =>
        bg === 'white'
          ? 'bg-white'
          : bg === 'light'
            ? 'bg-gradient-to-b from-gray-50 to-white'
            : 'bg-gray-800'
      )
    ),
    html.div(
      attr.class('max-w-4xl w-full'),
      Query({
        request: url,
        load: async ({ request: markdownUrl }): Promise<string> => {
          const response = await fetch(markdownUrl)
          if (!response.ok) {
            throw new Error(
              'Failed to fetch markdown: ' +
                response.status +
                ' ' +
                response.statusText
            )
          }
          const markdown = await response.text()
          const rawHtml = await marked.parse(markdown, {
            async: true,
            gfm: true,
            breaks: true,
          })
          const cleanHtml = DOMPurify.sanitize(rawHtml, {
            ALLOWED_TAGS: [
              'h1',
              'h2',
              'h3',
              'h4',
              'h5',
              'h6',
              'p',
              'a',
              'ul',
              'ol',
              'li',
              'blockquote',
              'code',
              'pre',
              'strong',
              'em',
              'br',
              'hr',
              'table',
              'thead',
              'tbody',
              'tr',
              'th',
              'td',
              'img',
            ],
            ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
          })
          return cleanHtml
        },
        success: ({ value }) =>
          MapSignal(value, (cleanHtml: string) =>
            html.article(
              attr.class('prose prose-lg max-w-none'),
              attr.class(
                Value.map(background, (bg): string =>
                  bg === 'dark'
                    ? 'prose-invert [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_h4]:text-white [&_h5]:text-white [&_h6]:text-white [&_p]:text-gray-300 [&_li]:text-gray-300 [&_a]:text-teal-400 [&_a:hover]:text-teal-300 [&_strong]:text-white [&_code]:bg-gray-800 [&_code]:text-teal-400 [&_blockquote]:border-teal-400 [&_blockquote]:bg-gray-900 [&_blockquote]:text-gray-400'
                    : '[&_h1]:text-gray-900 [&_h2]:text-gray-900 [&_h3]:text-gray-800 [&_h4]:text-gray-800 [&_h5]:text-gray-700 [&_h6]:text-gray-700 [&_p]:text-gray-700 [&_li]:text-gray-700 [&_strong]:text-gray-900 [&_em]:text-gray-700'
                )
              ),
              // Headings - using arbitrary variant selectors [&_element]
              attr.class(
                '[&_h1]:text-4xl sm:[&_h1]:text-5xl [&_h1]:font-bold [&_h1]:tracking-tight [&_h1]:mb-6 [&_h1]:mt-0'
              ),
              attr.class(
                '[&_h2]:text-3xl sm:[&_h2]:text-4xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:mb-5 [&_h2]:mt-12 [&_h2]:border-b [&_h2]:border-gray-200 [&_h2]:pb-3'
              ),
              attr.class(
                '[&_h3]:text-2xl sm:[&_h3]:text-3xl [&_h3]:font-bold [&_h3]:tracking-tight [&_h3]:mb-4 [&_h3]:mt-10'
              ),
              attr.class(
                '[&_h4]:text-xl sm:[&_h4]:text-2xl [&_h4]:font-bold [&_h4]:tracking-tight [&_h4]:mb-3 [&_h4]:mt-8'
              ),
              attr.class(
                '[&_h5]:text-lg sm:[&_h5]:text-xl [&_h5]:font-bold [&_h5]:tracking-tight [&_h5]:mb-3 [&_h5]:mt-6'
              ),
              attr.class(
                '[&_h6]:text-base sm:[&_h6]:text-lg [&_h6]:font-bold [&_h6]:tracking-tight [&_h6]:mb-2 [&_h6]:mt-6'
              ),
              // Paragraphs and text
              attr.class('[&_p]:mb-6 [&_p]:leading-relaxed'),
              attr.class('[&_strong]:font-semibold'),
              // Links
              attr.class(
                '[&_a]:text-teal-600 [&_a]:no-underline [&_a:hover]:text-teal-700 [&_a:hover]:underline [&_a]:font-medium [&_a]:transition-colors'
              ),
              // Lists
              attr.class('[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-6'),
              attr.class('[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-6'),
              attr.class('[&_li]:mb-2 [&_li]:leading-relaxed'),
              // Blockquotes
              attr.class(
                '[&_blockquote]:border-l-4 [&_blockquote]:border-teal-500 [&_blockquote]:pl-6 [&_blockquote]:pr-4 [&_blockquote]:py-2 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:bg-gray-50 [&_blockquote]:rounded-r [&_blockquote]:my-6'
              ),
              // Code
              attr.class(
                '[&_code]:bg-gray-100 [&_code]:text-teal-700 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono'
              ),
              attr.class(
                '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-6 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:shadow-lg [&_pre]:border [&_pre]:border-gray-700 [&_pre]:my-6'
              ),
              attr.class(
                '[&_pre_code]:bg-transparent [&_pre_code]:text-gray-100 [&_pre_code]:p-0'
              ),
              // Tables
              attr.class(
                '[&_table]:w-full [&_table]:border-collapse [&_table]:my-6'
              ),
              attr.class(
                '[&_th]:bg-gray-100 [&_th]:text-gray-900 [&_th]:font-semibold [&_th]:p-3 [&_th]:text-left [&_th]:border [&_th]:border-gray-300'
              ),
              attr.class('[&_td]:p-3 [&_td]:border [&_td]:border-gray-300'),
              attr.class('[&_tr:nth-child(even)]:bg-gray-50'),
              // Images
              attr.class(
                '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:shadow-md [&_img]:my-6'
              ),
              // Horizontal rules
              attr.class('[&_hr]:border-gray-300 [&_hr]:my-8'),
              attr.innerHTML(cleanHtml)
            )
          ),
      })
    )
  )
}
